import { Component } from 'react';
import autoBind from 'react-autobind';
import classNames from 'classnames/bind';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import isEqual from 'lodash-es/isEqual';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { Modal } from 'react-bootstrap';

import * as watchlistActions from '../../../actions/watchlistActions';
import * as searchActions from '../../../actions/searchActions';

import { checkAllAssetsStatus, watchlistTopicsQueryCreator } from '../../../helpers/helpers';
import { pickParams } from '../../../helpers/commonHelpers';
import { queryItemWatchlistParser } from '../../../helpers/watchlistHelperFunctions';
import { filterSelectedTimeFilter } from '../../../helpers/timeFilterHelpers';
import { filterSelectedSentimentForStories } from '../../../helpers/storyHelpers';
import { SILVER } from '../../../data/permissions';
import withComponentName from '../../../decorators/withComponentName';

import SearchbarInner from '../../SearchbarComponents/SearchbarInner';
import WatchlistTopicsListItem from '../WatchlistTopicsListItem';
import WatchlistRightBarHeader from '../WatchlistRightBar/WathclistRightBarHeader';
import WatchlistTopicsUpdateSuggestion from '../../SubscriptionsComponents/WatchlistTopicsUpdateSuggestion';
import BluePreloader from '../../GeneralComponents/BluePreloader';

import Styles from './styles.module.scss';
import Tooltip from '../../Tooltip';
import PiwikContext from '../../../services/piwikContext';

const cx = classNames.bind(Styles);

export class WatchlistTopicsList extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      rendered: false,
      isShowAllTopic: false,
    };
  }

  componentDidMount() {
    this.setState({
      rendered: true,
    });
  }

  componentDidUpdate(prevProps) {
    const {
      activeWatchlistTopics,
      storyFilters,
      activeWatchlist,
      actions,
      isFilingsPage,
    } = this.props;

    if (
      ((!isEqual(activeWatchlistTopics, prevProps.activeWatchlistTopics)
      || (!isEqual(storyFilters, prevProps.storyFilters) && activeWatchlist.id))
      && !isFilingsPage)
      || (!isFilingsPage && isFilingsPage !== prevProps.isFilingsPage)
    ) {
      actions.fetchStoriesBegin();
    }
  }

  // loading stories for search page, wl guest users
  storiesLoader(checkFreezingStories) {
    // terminate function if stories was frozen
    const {
      actions,
      activeWatchlist,
      token,
      freezeStories,
      storyFilters,
      stories,
      guestStories,
      activeWatchlistTopics,
      accessLevels,
    } = this.props;

    if ((checkFreezingStories && freezeStories) || !storyFilters.conflictsChecked) return;

    const storyData = () => (stories.length ? stories : guestStories);

    if (storyData().length === 0) actions.activatePreloader();

    const hasSilverAccess = accessLevels.includes(SILVER);
    const params = pickParams(
      { ...storyFilters },
      'categories', 'order_by', 'selected_time_filter', 'languages', 'min_score',
      'blacklisted_domain_ids', 'whitelisted_domain_ids', 'domains_filtering_mode', 'translate_to', 'all_languages',
      ...filterSelectedTimeFilter(storyFilters.selected_time_filter),
      ...filterSelectedSentimentForStories(storyFilters, hasSilverAccess),
    );

    params.premium_only = storyFilters.premium_content_only;
    params.fold_similar_stories = storyFilters.group_similar_stories;
    params.include_all_topics = checkAllAssetsStatus(activeWatchlistTopics);

    if (storyData().length) actions.updateWatchlistPageBegin();

    const query = watchlistTopicsQueryCreator(
      // Include no more than 33 topics
      // to not exceed URL length limit (2048 characters)
      activeWatchlistTopics.filter((item) => !item.hidden).slice(0, 33),
      token,
    );

    if (token) {
      actions.getWatchlistStories({
        watchlistId: activeWatchlist.id,
        params,
        nextPage: false,
        query,
      });
    } else {
      actions.loadGuestWatchlistStories({
        query,
        params: { ...params, query },
      });
      setTimeout(() => {
        actions.fetchStoriesSuccess();
      }, 900);
    }
  }

  toggleAllWatchlistTopics() {
    const {
      piwikEnabled,
      activeWatchlistTopics,
      token,
      activeWatchlist,
      actions,
    } = this.props;

    actions.checkAll(activeWatchlist.id, activeWatchlistTopics, token);

    if (piwikEnabled) {
      if (!checkAllAssetsStatus(activeWatchlistTopics)) {
        return _paq.push(['trackEvent', 'WL page', 'Select all ON']);
      }
      _paq.push(['trackEvent', 'WL page', 'Select all OFF']);
    }
  }

  toggleShowAllTopic() {
    const { isShowAllTopic } = this.state;
    this.setState({
      isShowAllTopic: !isShowAllTopic,
    });
  }

  render() {
    const {
      activeWatchlistTopics,
      activeWatchlist,
      actions,
      client,
      searchbarDisabled: disabled,
      piwikEnabled,
      activateFilingsPreloader,
      width,
      searchBarActive,
      promoHeight,
      watchlistUpdateBegin,
      wlPageLoadingStep,
    } = this.props;

    const { rendered, isShowAllTopic } = this.state;

    const activeAllBtnClass = cx({
      assets_checkbox: true,
      assets_checkbox_all: true,
      active: checkAllAssetsStatus(activeWatchlistTopics),
    });
    const activeWatchlistSearchClass = cx('active_watchlist_search', {
      // keeping `rendered` flag to prevent server side rendering end client rendering conflict
      disabled: rendered && disabled,
      active_search_bar: searchBarActive,
    });

    activeWatchlistTopics.sort((a, b) => {
      let { name: firstName } = a.query ? queryItemWatchlistParser(a.query) : a;
      let { name: lastName } = b.query ? queryItemWatchlistParser(b.query) : b;
      firstName = firstName.toLowerCase();
      lastName = lastName.toLowerCase();

      return firstName < lastName ? -1 : Number(firstName !== lastName);
    });
    const isOneTopicSelected = activeWatchlistTopics.some(({ hidden }) => !hidden);
    const tooltipTopOffset = width < 1024 ? 'calc(-100% - 10px))' : 'calc(-100% - 30px))';

    return (
      <div className={Styles.assets_wrapper}>
        <div className={Styles.assets_header}>
          <div className={Styles.assets_header_mobile}>
            <WatchlistRightBarHeader />
          </div>
          <div className={`${Styles.assets_top_wrapper} ${wlPageLoadingStep !== 'LOADING_WL' ? 'd-flex' : 'd-none'} justify-content-between`}>
            <div
              className={activeAllBtnClass}
              onClick={this.toggleAllWatchlistTopics}
            >
              <div className={Styles.checkbox_inner}>
                <FontAwesomeIcon icon={faCheck} />
              </div>
            </div>
            <div className={activeWatchlistSearchClass} style={searchBarActive && width > 767 ? { marginTop: `${promoHeight}px` } : {}}>
              <SearchbarInner
                searchStyles={width >= 1024 ? ([
                  'search_bar_full_width',
                  'multiple_slide_search',
                  'slide-left-bar',
                ]) : ([
                  'fixed_searchbar',
                  'multiple_slide_search',
                  'slide-left-bar',
                  'edit_watchlist_mobile',
                ])}
                fullWidth={width >= 1024}
                multipleSearch
                client={client}
                actions={actions}
                submitAddText
                placeholderAddText
                piwikEnabled={piwikEnabled}
                searchbarType="editWatchlistsSearchbar"
                isWatchlist
              />
            </div>
            {(rendered && disabled) && (
              <Tooltip
                id="watchlist-200-topic-tooltip"
                className={cx('wl_limit_tooltip_icon', 'wl_limit_tooltip_message')}
                renderOutside={width >= 1024}
                skipSmall
                hideWithOverflow
                contentStyle={{
                  transform: `translate(calc(-120% + 10px), ${tooltipTopOffset}`,
                  zIndex: '9494995',
                  width: '207px',
                  textAlign: 'center',
                }}
              >
                <PiwikContext.Consumer>
                  {(value) => (
                    <WatchlistTopicsUpdateSuggestion piwikEnabled={value.piwikEnabled} />
                  )}
                </PiwikContext.Consumer>
              </Tooltip>
            )}
          </div>
        </div>
        <ul className={Styles.assets_list}>
          {Boolean(activeWatchlistTopics) && (
            activeWatchlistTopics
              .slice(0, isShowAllTopic ? undefined : 5)
              .map((assetItem) => (
                <WatchlistTopicsListItem
                  key={assetItem.id}
                  watchlistId={activeWatchlist.id}
                  asset={assetItem}
                  piwikEnabled={piwikEnabled}
                />
              ))
          )}
        </ul>
        <div
          className={
            cx('assets_collaps_holder', { show: activeWatchlistTopics && (activeWatchlistTopics || []).length > 5 })
          }
        >
          <div className={Styles.assets_collaps} onClick={this.toggleShowAllTopic}>
            {
              isShowAllTopic ? (
                <>
                  Show less
                  <FontAwesomeIcon icon={faChevronUp} />
                </>
              ) : (
                <>
                  Show more
                  <FontAwesomeIcon icon={faChevronDown} />
                </>
              )
            }
          </div>
        </div>
        {!activateFilingsPreloader && (!activeWatchlistTopics.length || !isOneTopicSelected) && (
          <div className={Styles.assets_no_topics}>
            <FontAwesomeIcon icon={faExclamationCircle} />
            <div className={Styles.no_topic_text}>
              Please add or select at least one topic in
              the watchlist to get relevant news and analytics
            </div>
          </div>
        )}
        <Modal
          show={watchlistUpdateBegin}
          className={cx('wl-edit-wait-modal')}
          centered
        >
          <div className={Styles['wl-edit-holder']}>
            <div className={Styles['wl-edit-text']}>
              Adding to&nbsp;
              {activeWatchlist?.name}
            </div>
            <div className={Styles['wl-edit-loader']}>
              <BluePreloader active />
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = ({
  subscriptions,
  watchlistReducer,
  storyFilters,
  searchReducers,
  stories,
  common,
  promobar,
}) => {
  const { permissions, permissionsInitialized } = subscriptions;
  const {
    activeWatchlistTopics,
    activeWatchlist,
    userToken,
    activeWatchlistStories,
    activateFilingsPreloader,
    watchlistUpdateBegin,
  } = watchlistReducer;
  const {
    max_watchlist_size: maxWatchlistSize,
  } = permissions;

  return {
    activeWatchlist,
    activeWatchlistTopics,
    storyFilters,
    token: userToken,
    freezeStories: stories.freeze_stories,
    stories: activeWatchlistStories,
    guestStories: searchReducers.stories,
    searchbarDisabled: !permissionsInitialized || activeWatchlistTopics.length >= maxWatchlistSize,
    accessLevels: subscriptions.permissions.access_levels,
    activateFilingsPreloader,
    width: common.width,
    searchBarActive: searchReducers.searchBarActive,
    promoHeight: promobar?.height,
    watchlistUpdateBegin,
    wlPageLoadingStep: watchlistReducer.wlPageLoadingStep,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({ ...watchlistActions, ...searchActions }, dispatch),
});

export default withComponentName(
  connect(mapStateToProps, mapDispatchToProps)(WatchlistTopicsList),
);
